/**
 * Paged reads for relations an aggregate is derived from.
 *
 * PostgREST caps a response at 1,000 rows. A list that comes back capped reads
 * as a short list, which the next page fixes; an *aggregate* that comes back
 * capped reads as a wrong number, and nothing downstream can tell. That is not
 * theoretical here — the inventory dashboard totalled 1,000 of its 1,343
 * products and every card under it was short by the remainder while presenting
 * as a complete answer.
 *
 * Two properties make a walk trustworthy, and both are the caller's to supply:
 *
 * 1. A TOTAL order. Successive `.range()` windows are only successive slices of
 *    one sequence if the full result has one order; `orderListPage` and
 *    `tiebreakListOrder` in `./list-order.ts` append the unique key that makes
 *    it so.
 * 2. A page size strictly below the server cap. A page sized at the cap cannot
 *    distinguish a full page from a truncated one — the server clips it, the
 *    page arrives short, and the walk stops early with exactly the silent
 *    truncation it was added to prevent.
 */

/** Rows per request while walking a relation. Kept well under the 1,000 cap. */
const READ_PAGE_SIZE = 500;

/** Rows one walk may accumulate before it refuses to continue. */
const MAX_READ_ROWS = 50_000;

export interface ReadAllPagesOptions {
  /** Message for the error thrown once `maxRows` is passed. */
  limitMessage?: (maxRows: number) => string;
  maxRows?: number;
  pageSize?: number;
}

function defaultLimitMessage(maxRows: number): string {
  return `This view reads at most ${maxRows.toLocaleString()} rows. Narrow the range and try again.`;
}

/**
 * Walks `fetchPage` to the end of the relation and returns every row.
 *
 * Past `maxRows` it throws rather than returning what it has: a total computed
 * from part of the data is worse than no total, because it is indistinguishable
 * from a real one.
 */
export async function readAllPages<TRow>(
  fetchPage: (params: { limit: number; offset: number }) => Promise<TRow[]>,
  options?: ReadAllPagesOptions,
): Promise<TRow[]> {
  const pageSize = options?.pageSize ?? READ_PAGE_SIZE;
  const maxRows = options?.maxRows ?? MAX_READ_ROWS;
  const limitMessage = options?.limitMessage ?? defaultLimitMessage;
  const rows: TRow[] = [];

  for (let offset = 0; ; offset += pageSize) {
    const page = await fetchPage({ limit: pageSize, offset });
    rows.push(...page);

    if (rows.length > maxRows) {
      throw new Error(limitMessage(maxRows));
    }
    if (page.length < pageSize) {
      return rows;
    }
  }
}
